import { useEffect, useState } from 'react';
import { ArrowRightLeft, UserPlus, Clock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { translate } from '../../lib/translations';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';

interface PlayerTransactionHistoryProps {
  playerId: number;
  leagueId: number | null;
  language: 'fr' | 'en';
}

type TransactionType = 'trade' | 'signing';

interface Transaction {
  id: string;
  type: TransactionType;
  date: string;
  /** For trades: counterpart team manager username */
  counterpartUsername?: string;
  /** Side in the trade (sent or received) */
  side?: 'sent' | 'received';
  /** Manager who signed the player */
  signingUsername?: string;
}

function formatDate(dateStr: string, language: 'fr' | 'en') {
  try {
    return new Intl.DateTimeFormat(language === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(dateStr));
  } catch {
    return dateStr;
  }
}

async function loadTransactions(
  playerId: number,
  leagueId: number
): Promise<Transaction[]> {
  const results: Transaction[] = [];

  // 1. Fetch trades involving this player
  const { data: tradeRows, error: tradeError } = await supabase
    .from('gettradeshistory_players_and_users')
    .select(
      'th_id, trades_id, time_stamp, is_accepted, sender_user_id, sender_username, receiver_user_id, receiver_username, sender_player_id, receiver_player_id, sender_goalie_id, receiver_goalie_id'
    )
    .eq('leagueid', leagueId)
    .eq('is_accepted', true)
    .or(
      `sender_player_id.eq.${playerId},receiver_player_id.eq.${playerId},sender_goalie_id.eq.${playerId},receiver_goalie_id.eq.${playerId}`
    )
    .order('time_stamp', { ascending: false });

  if (!tradeError && tradeRows) {
    // Deduplicate by trades_id (multiple rows per transaction)
    const seenTradesIds = new Set<number>();
    for (const row of tradeRows) {
      const txId = row.trades_id ?? row.th_id;
      if (seenTradesIds.has(txId)) continue;
      seenTradesIds.add(txId);

      const isSender =
        row.sender_player_id === playerId || row.sender_goalie_id === playerId;

      results.push({
        id: `trade-${txId}`,
        type: 'trade',
        date: row.time_stamp,
        counterpartUsername: isSender ? row.receiver_username : row.sender_username,
        side: isSender ? 'sent' : 'received',
      });
    }
  }

  // 2. Fetch free agent signings
  const { data: signingRows, error: signingError } = await supabase
    .from('freeagentsigninghistory')
    .select('fa_signing_id, league_id, player_id, user_id, created_at')
    .eq('league_id', leagueId)
    .eq('player_id', playerId)
    .order('created_at', { ascending: false });

  if (!signingError && signingRows && signingRows.length > 0) {
    const userIds = Array.from(new Set((signingRows as {user_id: number}[]).map((r) => r.user_id)));
    const { data: users } = await supabase
      .from('users')
      .select('user_id, username')
      .in('user_id', userIds);
    const usersMap: Record<number, string> = {};
    (users as { user_id: number; username: string }[] | null ?? []).forEach((u) => {
      usersMap[u.user_id] = u.username;
    });

    for (const row of signingRows as { fa_signing_id: number; user_id: number; created_at: string }[]) {
      results.push({
        id: `signing-${row.fa_signing_id}`,
        type: 'signing',
        date: row.created_at,
        signingUsername: usersMap[row.user_id] ?? `#${row.user_id}`,
      });
    }
  }

  // Sort all transactions by date descending
  results.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return results;
}

export function PlayerTransactionHistory({
  playerId,
  leagueId,
  language,
}: PlayerTransactionHistoryProps) {
  const { isStandardFlexible, isLoading: modeLoading } = useLeagueMode();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leagueId) {
      setLoading(false);
      return;
    }

    // Si le mode ligue est en cours de résolution, attendre
    if (modeLoading) {
      return;
    }

    // Ne pas charger l'historique des transactions pour les ligues `standard_flexible`
    if (isStandardFlexible) {
      setLoading(false);
      setTransactions([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    loadTransactions(playerId, leagueId)
      .then((data) => {
        if (!cancelled) {
          setTransactions(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('Erreur chargement transactions joueur:', err);
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [playerId, leagueId, isStandardFlexible, modeLoading]);

  if (!leagueId) {
    return (
      <div className="text-muted text-center py-4">
        {translate('player.detail.analytics.no.league', language)}
      </div>
    );
  }

  // Cacher complètement le composant pour les ligues standard_flexible
  if (!modeLoading && isStandardFlexible) return null;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-4">
        <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
        <span className="text-muted">{translate('loading', language)}</span>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="text-muted text-center py-4">
        <Clock size={36} className="mb-2 opacity-50" />
        <div>{translate('player.detail.analytics.no.transactions', language)}</div>
      </div>
    );
  }

  return (
    <ul className="list-group list-group-flush">
      {transactions.map((tx) => (
        <li key={tx.id} className="list-group-item px-0">
          <div className="d-flex align-items-start gap-3">
            <div className="mt-1">
              {tx.type === 'trade' ? (
                <ArrowRightLeft size={16} className="text-primary" />
              ) : (
                <UserPlus size={16} className="text-success" />
              )}
            </div>
            <div className="flex-grow-1">
              <div className="fw-semibold">
                {tx.type === 'trade'
                  ? translate(
                      tx.side === 'sent'
                        ? 'player.detail.analytics.trade.sent'
                        : 'player.detail.analytics.trade.received',
                      language
                    ).replace('{username}', tx.counterpartUsername ?? '—')
                  : translate('player.detail.analytics.signing', language).replace(
                      '{username}',
                      tx.signingUsername ?? '—'
                    )}
              </div>
              <div className="text-muted small">{formatDate(tx.date, language)}</div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
